<template>
  <div>
    <div class="mb-4 flex justify-between items-center">
      <h2 class="text-lg font-semibold text-gray-900">Patient BCDA Records</h2>
      <ExportMenu
        :disabled="!rowData.length"
        :exports="[{ type: 'excel', action: exportToExcel }]"
      />
    </div>

    <div style="height: 500px;">
      <AgGridVue
        :theme="compactListTheme"
        style="width: 100%; height:100%"
        :column-defs="columnDefs"
        :row-data="rowData"
        :default-col-def="defaultColDef"
        :grid-options="gridOptions"
        :loading="loading"
        @grid-ready="onGridReady"
      />
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import axios from 'axios';
import { AgGridVue } from 'ag-grid-vue3';
import {compactListTheme} from '@/utils/common';
import CC_ToolTip from "@/components/widgetcomponents/AG_Grid_Custom_Cells/CC_ToolTip.vue";
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';

export default defineComponent({
  name: 'Details_Patient_BCDA_Table',
  computed: {
    compactListTheme() {
      return compactListTheme
    },
  },
  components: {
    AgGridVue,
    ExportMenu,
  },
  props: {
    patientIdentifier: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      rowData: [],
      gridApi: null,
      gridOptions: {
        suppressAggFuncInHeader: true,
        domLayout: this.autoHeight ? 'autoHeight' : null,
        treeData: true,
        animateRows: true,
        groupDefaultExpanded: -1, // Expand all groups by default
        treeDataChildrenField:'lines',
        getRowClass: (params) => {
          return params.node.group ? 'bg-gray-100 font-semibold' : '';
        },
        autoGroupColumnDef: {
          field: 'clm_id',
          headerName: 'Current Claim Unique ID',
          cellRenderer: 'agGroupCellRenderer',
          cellRendererParams: {
            suppressCount:true,
            innerRenderer: (params) => {
              if (params.node.group) {
                // Parent row - show claim ID
                return `${params.data.clm_id}`;
              } else {
                // Child row - show revenue center code
                return `Line: ${params.data.clm_line_num}`;
              }
            }
          }
        }
      },
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
      },

      columnDefs: [
        {
          field:'clm_thru_dt',
          sort:'desc',
          hide:true
        },
        {
          field:"clm_line_num",
          sort:'asc',
          hide:true,
        },
        {
          field:'clm_type_cd',
          headerName:'Claim Type',
          valueFormatter: (params)=>{
            return params.value === '10' ? 'HHA' :
                   params.value === '20' ? 'Non Swing Bed SNF Claim' :
                   params.value === '30' ? 'Swing Bed SNF Claim' :
                   params.value === '40' ? 'Outpatient Claim' :
                   params.value === '50' ? 'Hospice Claim' :
                   params.value === '60' ? 'Inpatient Claim' :
                   params.value === '61' ? 'Inpatient `Full Encounter` Claim' :
                   params.value === '72' ? 'DMEPOS Claim' :
                   params.value === '81' ? 'DME Claim' :
                   params.value === '82' ? 'DMERC/DMEPOS Claim' :
                   params.value === '61' ? 'Inpatient `Full Encounter` Claim' :
                   params.value === 'PDE' ? 'Part D Drug Claim' :
                   params.value === '71' ? 'Professional' : params.value;
          }
        },
        {
          field:'tin',
          headerName:'TIN'
        },
        {
          field: 'site_name',
          headerName: 'Provider Name',
          cellClass: 'sensitive',
          valueGetter:(params)=>{
            return params.value ? params.value :
              params.data.npi_last_name ? params.data.npi_last_name + ', ' + params.data.npi_first_name :
                ''
          },
          // valueFormatter: (params) => {
          //   return params.node.group ? params.value + ' (' + params.data.prvdr_oscar_num + ')' : '';
          // }
        },
        {
          field: 'clm_thru_dt',
          headerName: 'From',
          valueGetter: (params) => {
            return params.node.group ? params.data.clm_from_dt : params.data.clm_line_from_dt;
          },
          valueFormatter: this.dateFormatter
        },
        {
          field: 'clm_line_thru_dt',
          headerName: 'Through',
          valueGetter: (params) => {
            return params.node.group ? params.data.clm_thru_dt : params.data.clm_line_thru_dt;
          },
          valueFormatter: this.dateFormatter
        },
        {
          field: 'clm_dgns_cds',
          headerName: 'Diagnosis Codes',
          cellClass:'font-normal',
          cellRenderer: CC_ToolTip,
          cellRendererParams: (params) => {
            let diagnosisCodes = params.value || [];

            // Handle JSON array string from SQL array_agg
            if (typeof diagnosisCodes === 'string') {
              try {
                // Try to parse as JSON first
                diagnosisCodes = JSON.parse(diagnosisCodes);
              } catch (e) {
                // Fallback to comma-separated parsing
                diagnosisCodes = diagnosisCodes.split(',').map(code => code.trim());
              }
            }

            const filteredCodes = diagnosisCodes.filter(code => code && code.trim() !== '');
            const formattedText = filteredCodes
              .map((code, index) => `<strong>${index + 1}.</strong> ${code}`)
              .join('<br>');

            return {
              displayText: diagnosisCodes. filter(code => code && code.trim() !== '').join(', '),
              tooltipContent: formattedText,
              hide: !params.node.group
            };
          }
        },
        {
          field: 'clm_line_prod_rev_ctr_cd',
          headerName: 'Rev Ctr Code'
        },
        {
          field: 'clm_line_hcpcs_cd',
          headerName: 'HCPCS Code'
        },
        {
          field: 'description',
          headerName: 'HCPCS Description'
        },
        {
          field: 'clm_efctv_dt',
          headerName: 'Effective Date',
          valueFormatter: this.dateFormatter
        },
        {
          field: 'clm_pmt_amt',
          headerName: 'Allowed Chg',
          valueGetter: (params) => {
            return params.node.group ? params.data.clm_pmt_amt : params.data.clm_line_alowd_chrg_amt;
          },
        },
      ]
    };
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const response = await axios.post('api/returnpatientdata/', {
          data_type: 'bcda',
          patient_identifier: this.patientIdentifier,
        });

        // Transform the data to create tree structure
        this.rowData = this.transformToTreeData(response.data || []);
      } catch (error) {
        console.error('Error fetching tree data:', error);
        this.rowData = [];
      } finally {
        this.loading = false;
      }
    },
    transformToTreeData(data) {
      // Group data by cur_clm_uniq_id
      const groupedData = {};

      data.forEach(record => {
        const claimId = record.clm_id;

        if (!groupedData[claimId]) {
          groupedData[claimId] = {
            id: claimId,
            clm_id: claimId,
            clm_line_num:record.clm_line_num,
            clm_pmt_amt: record.clm_pmt_amt,
            prvdr_oscar_num: record.prvdr_oscar_num,
            site_name: record.site_name,
            bene_mbi_id: record.bene_mbi_id,
            clm_from_dt: record.clm_from_dt,
            clm_thru_dt: record.clm_thru_dt,
            clm_type_cd: record.clm_type_cd,
            clm_efctv_dt: record.clm_efctv_dt,
            clm_dgns_cds: record.clm_dgns_cds,
            lines: []
          };
        }

        // Add revenue center line as child
        groupedData[claimId].lines.push({
          clm_line_hcpcs_cd: record.clm_line_hcpcs_cd,
          description: record.description,
          clm_line_prod_rev_ctr_cd: record.clm_line_prod_rev_ctr_cd,
          clm_line_from_dt: record.clm_line_from_dt,
          clm_line_thru_dt: record.clm_line_thru_dt,
          clm_line_instnl_rev_ctr_dt: record.clm_line_instnl_rev_ctr_dt,
          ...record
        });
      });

      // Convert grouped object to array
      return Object.values(groupedData);
    },
    onGridReady(params) {
      this.gridApi = params.api;
      params.api.sizeColumnsToFit();
      window.addEventListener('resize', this.onWindowResize);
    },

    exportToExcel() {
      if (this.gridApi) {
        if (this.$root?.isDemoMode) return; // Demo Mode: block raw-data export
        this.gridApi.exportDataAsExcel({
          fileName: `patient_cclf_parta_${this.patientIdentifier}_${new Date().toISOString().split('T')[0]}.xlsx`,
          sheetName: 'CCLF Part A'
        });
      }
    },
    onWindowResize() {
      if (this.gridApi) {
        this.gridApi.sizeColumnsToFit();
      }
    },
    dateFormatter(params) {
      if (!params.value) return '';

      const dateStr = params.value.toString();

      // Handle YYYY-MM-DD format
      if (dateStr.includes('-')) {
        const [year, month, day] = dateStr.split('-');
        return `${month}/${day}/${year}`;
      }

      return params.value;
    }
  },
  mounted() {
    this.fetchData();
  }
});
</script>

<style scoped>

</style>
